import { useEffect, useEffectEvent, useRef, useState } from "react";

import { recolorNestingSvg } from "#modules/layoutsV2/domain/recolorNestingSvg";

export type NestingSvgStatus = "loading" | "colored" | "fallback" | "error";

type Params = {
  svgUrl: string;
  itemIds: string[];
  isEnabled: boolean;
  onUrlExpired: () => void;
};

type FetchedSvg = { svgUrl: string; markup: string | null; isExpired: boolean };

const EXPIRED_URL_STATUSES = new Set([400, 401, 403]);

const fetchRecoloredSvg = async (
  svgUrl: string,
  itemIds: string[],
  signal: AbortSignal,
): Promise<FetchedSvg> => {
  try {
    const response = await fetch(svgUrl, { signal });
    if (!response.ok) {
      return { svgUrl, markup: null, isExpired: EXPIRED_URL_STATUSES.has(response.status) };
    }
    return { svgUrl, markup: recolorNestingSvg(await response.text(), itemIds), isExpired: false };
  } catch {
    return { svgUrl, markup: null, isExpired: false };
  }
};

export const useNestingSvg = ({ svgUrl, itemIds, isEnabled, onUrlExpired }: Params) => {
  const [fetched, setFetched] = useState<FetchedSvg | null>(null);
  const [hasImageFailed, setHasImageFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const hasRequestedFreshUrl = useRef(false);
  const itemIdsKey = itemIds.join("|");
  const requestFreshUrl = useEffectEvent(onUrlExpired);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const controller = new AbortController();
    void fetchRecoloredSvg(svgUrl, itemIdsKey.split("|"), controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return;
      }
      const shouldRefreshUrl = result.isExpired && !hasRequestedFreshUrl.current;
      if (shouldRefreshUrl) {
        hasRequestedFreshUrl.current = true;
        requestFreshUrl();
        return;
      }
      setFetched(result);
    });
    return () => controller.abort();
  }, [svgUrl, itemIdsKey, isEnabled, attempt]);

  const retry = () => {
    hasRequestedFreshUrl.current = false;
    setHasImageFailed(false);
    setFetched(null);
    setAttempt((current) => current + 1);
    onUrlExpired();
  };

  const isCurrentUrlFetched = fetched?.svgUrl === svgUrl;
  const status: NestingSvgStatus = hasImageFailed
    ? "error"
    : !isEnabled
      ? "fallback"
      : !isCurrentUrlFetched
        ? "loading"
        : fetched.markup === null
          ? "fallback"
          : "colored";

  return {
    status,
    markup: isCurrentUrlFetched ? fetched.markup : null,
    onImageError: () => setHasImageFailed(true),
    retry,
  };
};
